import React, { createContext, useContext, useReducer, useEffect, useRef, type ReactNode } from 'react'
import { APIWeb } from '@/api'
import { Toast } from 'react-vant'
import i18n from '@/i18n'

// 币种选择相关类型定义
interface CurrencyItem {
  icon: string
  name: string
  description: string
  symbol: string
  chainList: any[]
}

interface NetworkItem {
  chainId?: string
  chainName?: string
  symbol?: string
}

interface CurrencySelection {
  selectedCurrency: CurrencyItem | null
  currentNetwork: NetworkItem | null
}

// 卡片列表项类型（基于API响应 getUserCardListV2CardListItemResp）
export interface CardListItem {
  /** 卡可用余额（未下卡为0） */
  available_balance?: number
  /** 是否已下卡 */
  card_issued?: boolean
  /** 卡产品ID */
  card_product_id?: number
  /** 卡形态 virtual_card/physical_card */
  form_factor?: string
  /** 卡ID（若已下卡）或卡申请ID（若未下卡） */
  item_id?: number | string
  /** 掩码卡号（未下卡为空） */
  masked_pan?: string
  /** 卡状态（已下卡返回卡状态，未下卡返回申请状态） */
  status?: string
  /** 卡片图片 */
  image?: string
  [property: string]: any
}

// 状态类型定义
interface AppState {
  userInfo: MemberInfoResponse | null
  // 用户持卡列表（虚拟卡和实体卡统一管理）
  cardList: CardListItem[]
  hasCard: boolean | null
  // 商家配置
  merchantConfig: GetBusinessConfigResponse | null
  currencySelection: CurrencySelection
  loading: {
    userInfo: boolean
    cardList: boolean
    merchantConfig: boolean
  }
  error: {
    userInfo: string | null
    cardList: string | null
    merchantConfig: string | null
  }
}

// Action 类型定义
type AppAction =
  | { type: 'SET_USER_INFO_LOADING'; payload: boolean }
  | { type: 'SET_USER_INFO_SUCCESS'; payload: MemberInfoResponse }
  | { type: 'SET_USER_INFO_ERROR'; payload: string }
  // 卡片列表 Actions
  | { type: 'SET_CARD_LIST_LOADING'; payload: boolean }
  | { type: 'SET_CARD_LIST_SUCCESS'; payload: { cardList: CardListItem[]; hasCard: boolean } }
  | { type: 'SET_CARD_LIST_ERROR'; payload: string }
  // 商家配置 Actions
  | { type: 'SET_MERCHANT_CONFIG_LOADING'; payload: boolean }
  | { type: 'SET_MERCHANT_CONFIG_SUCCESS'; payload: GetBusinessConfigResponse }
  | { type: 'SET_MERCHANT_CONFIG_ERROR'; payload: string }
  | { type: 'SET_SELECTED_CURRENCY'; payload: CurrencyItem }
  | { type: 'SET_CURRENT_NETWORK'; payload: NetworkItem }
  | { type: 'CLEAR_CURRENCY_SELECTION' }
  | { type: 'CLEAR_USER_INFO' }
  | { type: 'CLEAR_CARD_LIST' }
  | { type: 'CLEAR_MERCHANT_CONFIG' }
  | { type: 'CLEAR_ALL' }
  | { type: 'HYDRATE_FROM_STORAGE'; payload: Partial<AppState> }

// 初始状态
const initialState: AppState = {
  userInfo: null,
  cardList: [],
  hasCard: null,
  merchantConfig: null,
  currencySelection: {
    selectedCurrency: null,
    currentNetwork: null
  },
  loading: {
    userInfo: false,
    cardList: false,
    merchantConfig: false
  },
  error: {
    userInfo: null,
    cardList: null,
    merchantConfig: null
  }
}

// Reducer
const appReducer = (state: AppState, action: AppAction): AppState => {
  switch (action.type) {
    case 'SET_USER_INFO_LOADING':
      return {
        ...state,
        loading: { ...state.loading, userInfo: action.payload },
        error: { ...state.error, userInfo: null }
      }
    
    case 'SET_USER_INFO_SUCCESS':
      return {
        ...state,
        userInfo: action.payload,
        loading: { ...state.loading, userInfo: false },
        error: { ...state.error, userInfo: null }
      }
    
    case 'SET_USER_INFO_ERROR':
      return {
        ...state,
        loading: { ...state.loading, userInfo: false },
        error: { ...state.error, userInfo: action.payload }
      }
    
    // 卡片列表 Cases
    case 'SET_CARD_LIST_LOADING':
      return {
        ...state,
        loading: { ...state.loading, cardList: action.payload },
        error: { ...state.error, cardList: null }
      }
    
    case 'SET_CARD_LIST_SUCCESS':
      return {
        ...state,
        cardList: action.payload.cardList,
        hasCard: action.payload.hasCard,
        loading: { ...state.loading, cardList: false },
        error: { ...state.error, cardList: null }
      }
    
    case 'SET_CARD_LIST_ERROR':
      return {
        ...state,
        loading: { ...state.loading, cardList: false },
        error: { ...state.error, cardList: action.payload }
      }
    
    // 商家配置 Cases
    case 'SET_MERCHANT_CONFIG_LOADING':
      return {
        ...state,
        loading: { ...state.loading, merchantConfig: action.payload },
        error: { ...state.error, merchantConfig: null }
      }
    
    case 'SET_MERCHANT_CONFIG_SUCCESS':
      return {
        ...state,
        merchantConfig: action.payload,
        loading: { ...state.loading, merchantConfig: false },
        error: { ...state.error, merchantConfig: null }
      }
    
    case 'SET_MERCHANT_CONFIG_ERROR':
      return {
        ...state,
        loading: { ...state.loading, merchantConfig: false },
        error: { ...state.error, merchantConfig: action.payload }
      }
    
    case 'SET_SELECTED_CURRENCY':
      return {
        ...state,
        currencySelection: {
          ...state.currencySelection,
          selectedCurrency: action.payload
        }
      }
    
    case 'SET_CURRENT_NETWORK':
      return {
        ...state,
        currencySelection: {
          ...state.currencySelection,
          currentNetwork: action.payload
        }
      }
    
    case 'CLEAR_CURRENCY_SELECTION':
      return {
        ...state,
        currencySelection: {
          selectedCurrency: null,
          currentNetwork: null
        }
      }
    
    case 'CLEAR_USER_INFO':
      return {
        ...state,
        userInfo: null,
        error: { ...state.error, userInfo: null }
      }
    
    case 'CLEAR_CARD_LIST':
      return {
        ...state,
        cardList: [],
        hasCard: null,
        error: { ...state.error, cardList: null }
      }
    
    case 'CLEAR_MERCHANT_CONFIG':
      return {
        ...state,
        merchantConfig: null,
        error: { ...state.error, merchantConfig: null }
      }
    
    case 'CLEAR_ALL':
      return {
        ...initialState
      }
    
    case 'HYDRATE_FROM_STORAGE':
      return {
        ...state,
        ...action.payload
      }
    
    default:
      return state
  }
}

// Context 类型定义
interface AppContextType {
  state: AppState
  actions: {
    fetchUserInfo: (force?: boolean) => Promise<void>
    fetchCardList: (force?: boolean, excludeClosed?: boolean, excludePending?: boolean) => Promise<void>
    fetchMerchantConfig: (force?: boolean) => Promise<void>
    refreshUserInfo: () => Promise<void>
    refreshCardList: (excludeClosed?: boolean, excludePending?: boolean) => Promise<void>
    refreshMerchantConfig: () => Promise<void>
    refreshAll: () => Promise<void>
    clearUserInfo: () => void
    clearCardList: () => void
    clearMerchantConfig: () => void
    clearAll: () => void
    setSelectedCurrency: (currency: CurrencyItem) => void
    setCurrentNetwork: (network: NetworkItem) => void
    clearCurrencySelection: () => void
  }
}

// 创建 Context
const AppStateContext = createContext<AppContextType | undefined>(undefined)

// 持久化相关常量
const STORAGE_KEY = 'app_state'

// 持久化工具函数
const saveToStorage = (state: AppState) => {
  try {
    const dataToSave = {
      userInfo: state.userInfo,
      cardList: state.cardList,
      hasCard: state.hasCard,
      merchantConfig: state.merchantConfig,
      currencySelection: state.currencySelection
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(dataToSave))
  } catch (error) {
    console.error('保存状态到本地存储失败:', error)
  }
}

const loadFromStorage = (): Partial<AppState> | null => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (!stored) return null
    
    const parsed = JSON.parse(stored)
    
    // 兼容旧数据格式：将 electronicCardInfo/physicalCardInfo 转换为 cardList
    let cardList: CardListItem[] = parsed.cardList || []
    let hasCard: boolean | null = parsed.hasCard ?? null
    
    // 如果是旧格式数据，尝试转换
    if (cardList.length === 0 && hasCard === null) {
      const legacyCards: CardListItem[] = []
      
      // 转换旧的虚拟卡数据
      if (parsed.electronicCardInfo) {
        legacyCards.push({
          item_id: parsed.electronicCardInfo.item_id || parsed.electronicCardInfo.id,
          pan: parsed.electronicCardInfo.card_no || parsed.electronicCardInfo.pan,
          balance: parsed.electronicCardInfo.balance,
          type: 'virtual_card'
        })
      }
      
      // 转换旧的实体卡数据
      if (parsed.physicalCardInfo) {
        legacyCards.push({
          item_id: parsed.physicalCardInfo.item_id || parsed.physicalCardInfo.id,
          pan: parsed.physicalCardInfo.card_no || parsed.physicalCardInfo.pan,
          balance: parsed.physicalCardInfo.balance,
          type: 'physical_card'
        })
      }
      
      if (legacyCards.length > 0) {
        cardList = legacyCards
        hasCard = true
      } else if (parsed.hasElectronicCard !== undefined || parsed.hasPhysicalCard !== undefined) {
        // 旧格式中明确标记了是否有卡
        hasCard = parsed.hasElectronicCard === true || parsed.hasPhysicalCard === true
      }
    }
    
    return {
      userInfo: parsed.userInfo,
      cardList,
      hasCard,
      merchantConfig: parsed.merchantConfig,
      currencySelection: parsed.currencySelection || {
        selectedCurrency: null,
        currentNetwork: null
      }
    }
  } catch (error) {
    console.error('从本地存储加载状态失败:', error)
    return null
  }
}

// Provider 组件
export const AppStateProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(appReducer, initialState)

  // 从本地存储恢复状态
  useEffect(() => {
    const storedState = loadFromStorage()
    if (storedState) {
      dispatch({ type: 'HYDRATE_FROM_STORAGE', payload: storedState })
    }
  }, [])

  // 状态变化时保存到本地存储
  useEffect(() => {
    // 关键：使用 hasCard !== null 判断，确保即使 cardList 为空也会保存
    if (
      state.userInfo || 
      state.hasCard !== null || 
      state.merchantConfig ||
      state.currencySelection.selectedCurrency
    ) {
      saveToStorage(state)
    }
  }, [state.userInfo, state.cardList, state.hasCard, state.merchantConfig, state.currencySelection])

  // 获取用户信息
  const fetchUserInfo = async (_force = false) => {
    try {
      dispatch({ type: 'SET_USER_INFO_LOADING', payload: true })
      const userInfo = await APIWeb.getUserInfo()
      dispatch({ type: 'SET_USER_INFO_SUCCESS', payload: userInfo })
    } catch (error: any) {
      dispatch({ type: 'SET_USER_INFO_ERROR', payload: error.message || '获取用户信息失败' })
      console.error('获取用户信息失败:', error)
    }
  }

  // 获取用户持卡列表
  const fetchCardList = async (_force = false, excludeClosed = true, excludePending = false) => {
    try {
      dispatch({ type: 'SET_CARD_LIST_LOADING', payload: true })
      const data = await APIWeb.getUserCardListV2({ exclude_closed: excludeClosed, exclude_pending: excludePending })
      
      const cardList = data?.list || []
      const hasCard = cardList.length > 0
      
      dispatch({ 
        type: 'SET_CARD_LIST_SUCCESS', 
        payload: { cardList, hasCard } 
      })
    } catch (error: any) {
      dispatch({ type: 'SET_CARD_LIST_ERROR', payload: error.message || '获取卡片列表失败' })
      console.error('获取卡片列表失败:', error)
    }
  }

  // 获取商家配置
  const fetchMerchantConfig = async (_force = false) => {
    try {
      dispatch({ type: 'SET_MERCHANT_CONFIG_LOADING', payload: true })
      const config = await APIWeb.getMerchantConfig()
      dispatch({ type: 'SET_MERCHANT_CONFIG_SUCCESS', payload: config })
    } catch (error: any) {
      dispatch({ type: 'SET_MERCHANT_CONFIG_ERROR', payload: error.message || '获取商家配置失败' })
      console.error('获取商家配置失败:', error)
    }
  }

  // 强制刷新用户信息
  const refreshUserInfo = async () => {
    await fetchUserInfo(true)
  }

  // 强制刷新卡片列表
  const refreshCardList = async (excludeClosed = true, excludePending = false) => {
    await fetchCardList(true, excludeClosed, excludePending)
  }

  // 强制刷新商家配置
  const refreshMerchantConfig = async () => {
    await fetchMerchantConfig(true)
  }

  // 刷新所有信息
  const refreshAll = async () => {
    await Promise.all([
      fetchUserInfo(true),
      fetchCardList(true),
      fetchMerchantConfig(true)
    ])
  }

  // 清除用户信息
  const clearUserInfo = () => {
    dispatch({ type: 'CLEAR_USER_INFO' })
  }

  // 清除卡片列表
  const clearCardList = () => {
    dispatch({ type: 'CLEAR_CARD_LIST' })
  }

  // 清除商家配置
  const clearMerchantConfig = () => {
    dispatch({ type: 'CLEAR_MERCHANT_CONFIG' })
  }

  // 清除所有信息
  const clearAll = () => {
    dispatch({ type: 'CLEAR_ALL' })
    localStorage.removeItem(STORAGE_KEY)
  }

  // 设置选中的币种
  const setSelectedCurrency = (currency: CurrencyItem) => {
    dispatch({ type: 'SET_SELECTED_CURRENCY', payload: currency })
  }

  // 设置当前网络
  const setCurrentNetwork = (network: NetworkItem) => {
    dispatch({ type: 'SET_CURRENT_NETWORK', payload: network })
  }

  // 清除币种选择
  const clearCurrencySelection = () => {
    dispatch({ type: 'CLEAR_CURRENCY_SELECTION' })
  }

  const contextValue: AppContextType = {
    state,
    actions: {
      fetchUserInfo,
      fetchCardList,
      fetchMerchantConfig,
      refreshUserInfo,
      refreshCardList,
      refreshMerchantConfig,
      refreshAll,
      clearUserInfo,
      clearCardList,
      clearMerchantConfig,
      clearAll,
      setSelectedCurrency,
      setCurrentNetwork,
      clearCurrencySelection
    }
  }

  return (
    <AppStateContext.Provider value={contextValue}>
      {children}
    </AppStateContext.Provider>
  )
}

// Hook 用于使用 Context
export const useAppState = () => {
  const context = useContext(AppStateContext)
  if (context === undefined) {
    throw new Error('useAppState must be used within an AppStateProvider')
  }
  return context
}

// 导出便捷的 hooks
export const useUserInfo = () => {
  const { state, actions } = useAppState()
  const retryCountRef = useRef(0)
  const lastFetchTimeRef = useRef(0)
  const maxRetries = 5
  const minRetryInterval = 1000 // 最小重试间隔 1 秒
  
  // 自动获取用户信息（如果没有缓存）
  useEffect(() => {
    const now = Date.now()
    const timeSinceLastFetch = now - lastFetchTimeRef.current
    
    // 条件检查
    const shouldFetch = 
      !state.userInfo &&                           // 没有用户信息
      !state.loading.userInfo &&                   // 没有正在加载
      retryCountRef.current < maxRetries &&        // 没有超过最大重试次数
      timeSinceLastFetch > minRetryInterval        // 距离上次请求超过最小间隔
    
    if (shouldFetch) {
      lastFetchTimeRef.current = now
      retryCountRef.current += 1
      
      actions.fetchUserInfo()
        .then(() => {
          // 成功：重置计数器
          retryCountRef.current = 0
        })
        .catch(() => {
          // 只在第5次失败时提示
          if (retryCountRef.current >= maxRetries) {
            Toast.info({
              position: 'top',
              message: i18n.t('获取用户信息失败'),
            })
          }
        })
    }
    
    // 成功获取到用户信息，重置计数器
    if (state.userInfo) {
      retryCountRef.current = 0
      lastFetchTimeRef.current = 0
    }
  }, [state.userInfo, state.loading.userInfo])

  return {
    userInfo: state.userInfo,
    loading: state.loading.userInfo,
    error: state.error.userInfo,
    actions: {
      refreshUserInfo: actions.refreshUserInfo,
      clearUserInfo: actions.clearUserInfo,
      fetchUserInfo: actions.fetchUserInfo
    }
  }
}

// 卡片列表 Hook
export const useCardList = () => {
  const { state, actions } = useAppState()
  const retryCountRef = useRef(0)
  const lastFetchTimeRef = useRef(0)
  const maxRetries = 5
  const minRetryInterval = 1000

  
  // 自动获取卡片列表（如果没有缓存）
  useEffect(() => {
    const now = Date.now()
    const timeSinceLastFetch = now - lastFetchTimeRef.current
    
    const shouldFetch = 
      state.cardList.length === 0 &&
      state.hasCard === null &&
      !state.loading.cardList &&
      retryCountRef.current < maxRetries &&
      timeSinceLastFetch > minRetryInterval
    
    if (shouldFetch) {
      lastFetchTimeRef.current = now
      retryCountRef.current += 1
      actions.fetchCardList()
    }
    
    // 成功获取到信息，重置计数器
    if (state.cardList.length > 0 || state.hasCard !== null) {
      retryCountRef.current = 0
      lastFetchTimeRef.current = 0
    }
  }, [state.cardList, state.hasCard, state.loading.cardList])

  // 获取虚拟卡列表（使用 form_factor 字段）
  const virtualCards = state.cardList.filter(card => card.form_factor === 'virtual_card')
  // 获取实体卡列表（使用 form_factor 字段）
  const physicalCards = state.cardList.filter(card => card.form_factor === 'physical_card')


  return {
    cardList: state.cardList,
    allCards: state.cardList, // 别名，方便使用
    virtualCards,
    physicalCards,
    hasCard: state.hasCard,
    hasVirtualCard: virtualCards.length > 0,
    hasPhysicalCard: physicalCards.length > 0,
    loading: state.loading.cardList,
    error: state.error.cardList,
    actions: {
      refreshCardList: actions.refreshCardList,
      clearCardList: actions.clearCardList,
      fetchCardList: actions.fetchCardList
    }
  }
}

// 兼容旧版 Hook（已废弃，建议使用 useCardList）
/** @deprecated 请使用 useCardList 替代 */
export const useElectronicCardInfo = () => {
  const { virtualCards, hasVirtualCard, loading, error, actions } = useCardList()
  
  return {
    cardInfo: virtualCards[0] || null,
    hasCard: hasVirtualCard,
    loading,
    error,
    actions: {
      refreshCardInfo: actions.refreshCardList,
      clearCardInfo: actions.clearCardList,
      fetchCardInfo: actions.fetchCardList
    }
  }
}

/** @deprecated 请使用 useCardList 替代 */
export const usePhysicalCardInfo = () => {
  const { physicalCards, hasPhysicalCard, loading, error, actions } = useCardList()
  
  return {
    cardInfo: physicalCards[0] || null,
    hasCard: hasPhysicalCard,
    loading,
    error,
    actions: {
      refreshCardInfo: actions.refreshCardList,
      clearCardInfo: actions.clearCardList,
      fetchCardInfo: actions.fetchCardList
    }
  }
}

// 商家配置 Hook
export const useMerchantConfig = () => {
  const { state, actions } = useAppState()
  const retryCountRef = useRef(0)
  const lastFetchTimeRef = useRef(0)
  const maxRetries = 5
  const minRetryInterval = 1000
  
  // 自动获取商家配置（如果没有缓存）
  useEffect(() => {
    const now = Date.now()
    const timeSinceLastFetch = now - lastFetchTimeRef.current
    
    const shouldFetch = 
      !state.merchantConfig &&
      !state.loading.merchantConfig &&
      retryCountRef.current < maxRetries &&
      timeSinceLastFetch > minRetryInterval
    
    if (shouldFetch) {
      lastFetchTimeRef.current = now
      retryCountRef.current += 1
      actions.fetchMerchantConfig()
    }
    
    // 成功获取到信息，重置计数器
    if (state.merchantConfig) {
      retryCountRef.current = 0
      lastFetchTimeRef.current = 0
    }
  }, [state.merchantConfig, state.loading.merchantConfig])

  return {
    merchantConfig: state.merchantConfig,
    loading: state.loading.merchantConfig,
    error: state.error.merchantConfig,
    actions: {
      refreshMerchantConfig: actions.refreshMerchantConfig,
      clearMerchantConfig: actions.clearMerchantConfig,
      fetchMerchantConfig: actions.fetchMerchantConfig
    }
  }
}

// 币种选择相关的便捷hook
export const useCurrencySelection = () => {
  const { state, actions } = useAppState()

  return {
    selectedCurrency: state.currencySelection.selectedCurrency,
    currentNetwork: state.currencySelection.currentNetwork,
    actions: {
      setSelectedCurrency: actions.setSelectedCurrency,
      setCurrentNetwork: actions.setCurrentNetwork,
      clearCurrencySelection: actions.clearCurrencySelection
    }
  }
}

// 导出类型定义供其他组件使用
export type { CurrencyItem, NetworkItem, CurrencySelection }